// @ts-strict-ignore
// eslint-disable-next-line no-restricted-imports
import type {Component, PanelLifecycleContext} from 'panel';
import {i18n, TOptions} from 'i18next';
import moment, {Moment} from 'moment';
import {Moment as MomentTimezone} from 'moment-timezone';

import type {Subscribable} from 'common/types/context/subscribable';

import type {Translatable, TranslationKey} from './index';

export class LocalizationStore implements PanelLifecycleContext, Subscribable {
  private connectedComponents = new Set<Component<any>>();
  private subscriptions = new Set<() => void>();

  private declare languageOverride: string;

  constructor(private i18next: i18n) {
    i18next.on(`languageChanged`, () => {
      this.updateComponents();

      moment.locale(this.language);
    });
  }

  get language() {
    return this.i18next.language;
  }

  /** Gets translation for a key with options. */
  t = (key: TranslationKey, opts?: TOptions): string => {
    // @ts-ignore -- type excessive; ok to disable here since we're wrapping it safely
    return this.i18next.t(key, {
      lng: this.languageOverride,
      ...opts,
    }) as string;
  };

  /** Maybe translates a string if it's a function. Otherwise, returns the original string. */
  print = (translatable: Translatable | undefined | null, forceLng?: string): string => {
    if (!translatable) {
      return ``;
    }
    this.languageOverride = forceLng;
    const result = typeof translatable === `function` ? translatable(this) : translatable;
    this.languageOverride = null;
    return result;
  };

  /**
   * Joins a list of translatables using the current locale.
   *
   * More info: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/ListFormat/ListFormat#options
   */
  formatList = (
    items: Translatable[],
    type?: Intl.ListFormatOptions[`type`],
    style?: Intl.ListFormatOptions[`style`],
  ) => {
    const translatedItems = items.map((item) => this.print(item));

    return new Intl.ListFormat(this.language, {type, style}).format(translatedItems);
  };

  /**
   * Returns the "and" conjunction word using the current locale.
   * NOTE: This is for edge cases where formatList doesn't work. Use formatList where at all possible.
   */
  getConjunctionWord = () => {
    const parts = new Intl.ListFormat(this.language, {type: `conjunction`, style: `long`}).formatToParts([`1`, `2`]);
    return parts.find((p) => p.type === `literal`)?.value?.trim() || ``;
  };

  /**
   * Formats a date using the current locale.
   *
   * More info: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat#options
   */
  formatDate = (date: Date | Moment, options: Intl.DateTimeFormatOptions) => {
    // Make sure we don't lose timezone information.
    if (moment.isMoment(date) && `tz` in date) {
      options.timeZone = (date as MomentTimezone).tz();
    }
    const jsDate = date instanceof Date ? date : date.toDate();

    return new Intl.DateTimeFormat(this.language, options).format(jsDate);
  };

  /**
   * Formats a date to parts using the current locale.
   *
   * More info: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/DateTimeFormat/DateTimeFormat#options
   */
  formatDateToParts = (date: Date | Moment, options: Intl.DateTimeFormatOptions) => {
    const jsDate = date instanceof Date ? date : date.toDate();

    return new Intl.DateTimeFormat(this.language, options).formatToParts(jsDate);
  };

  /**
   * Formats a relative time unit using the current locale.
   *
   * More info: https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Global_Objects/Intl/RelativeTimeFormat/RelativeTimeFormat#options
   */
  formatRelativeTime = (value: number, unit: Intl.RelativeTimeFormatUnit, options?: Intl.RelativeTimeFormatOptions) => {
    return new Intl.RelativeTimeFormat(this.language, options).format(value, unit);
  };

  /**
   * Returns a localized string representing the difference between 2 times, similar to Moment#from.
   */
  timeFrom = (fromTime: Date | Moment, toTime: Date | Moment) => {
    const from = fromTime instanceof Date ? moment(fromTime) : fromTime;
    const to = toTime instanceof Date ? moment(toTime) : toTime;

    // less than a minute ago; in less than a minute
    const diffSeconds = from.diff(to, `seconds`);
    if (Math.abs(diffSeconds) < 60) {
      return this.t(`time.less-than-minute-${from.isBefore(to) ? `past` : `future`}`);
    }

    // X minute(s) ago; in X minute(s)
    const diffMinutes = from.diff(to, `minutes`);
    if (Math.abs(diffMinutes) < 60) {
      return this.formatRelativeTime(diffMinutes, `minutes`);
    }

    // X hour(s) ago; in X hour(s)
    const diffHours = from.diff(to, `hours`);
    if (Math.abs(diffHours) < 24) {
      return this.formatRelativeTime(diffHours, `hours`);
    }

    // X day(s) ago; in X day(s)
    const diffDays = from.diff(to, `days`);
    const diffMonths = from.diff(to, `months`);
    if (Math.abs(diffMonths) < 1) {
      return this.formatRelativeTime(diffDays, `days`);
    }

    // X month(s) ago; in X month(s)
    const diffYears = from.diff(to, `years`);
    if (Math.abs(diffYears) < 1) {
      return this.formatRelativeTime(diffMonths, `months`);
    }

    // X year(s) ago; in X year(s)
    return this.formatRelativeTime(diffYears, `years`);
  };

  /**
   * Returns a localized string representing the difference between a time and now, similar to Moment#fromNow.
   */
  timeFromNow = (time: Date | Moment) => this.timeFrom(time, moment());

  public subscribe(callback: () => void): this {
    this.subscriptions.add(callback);
    return this;
  }

  public unsubscribe(callback: () => void): this {
    this.subscriptions.delete(callback);
    return this;
  }

  public bindToComponent(component: Component<any>): void {
    this.connectedComponents.add(component);
  }

  public unbindFromComponent(component: Component<any>): void {
    this.connectedComponents.delete(component);
  }

  updateComponents = () => {
    for (const component of this.connectedComponents) {
      component.update();
    }

    for (const subscription of this.subscriptions) {
      subscription();
    }
  };
}
